if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      pendingRequestUrl = null;
      failedSelectionRequest = null;
      handleSelectionRetry = (event) => {
        if (!event.target.closest('[data-selection-retry]') || !this.failedSelectionRequest) return;
        event.preventDefault();
        return this.renderProductInfo(this.failedSelectionRequest);
      };
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      sectionCache = new Map();
      sectionCacheTtl = 30000;
      intentTimer = undefined;
      loadingTimer = undefined;
      cacheResetUnsubscriber = undefined;
      // Start the section request when a shopper points at, touches or
      // focuses an option, so the change is usually ready by the click.
      handleOptionIntent = (event) => {
        const label = event.target.closest?.("variant-picker label[for]");
        const input = label
          ? document.getElementById(label.htmlFor)
          : event.target.closest?.("variant-picker input[type='radio']");
        if (!input || input.type !== "radio" || input.checked || !this.contains(input)) return;
        clearTimeout(this.intentTimer);
        if (event.type === "pointerover" && event.pointerType === "mouse") {
          this.intentTimer = setTimeout(() => this.prefetchOption(input), 80);
        } else {
          this.prefetchOption(input);
        }
      };
      cancelOptionIntent = (event) => {
        if (event.pointerType === "mouse") clearTimeout(this.intentTimer);
      };

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector("variant-picker");
      }

      get productMedia() {
        return this.querySelector(
          `[id^="MediaGallery-${this.dataset.section}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get productUrl() {
        return this.dataset.url;
      }

      get productId() {
        return this.getAttribute("data-product-id");
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get inventoryStatus() {
        return this.querySelector(`#Inventory-${this.dataset.section}`);
      }

      get sku() {
        return this.querySelector(`#Sku-${this.dataset.section}`);
      }

      get availability() {
        return this.querySelector(`#Availability-${this.dataset.section}`);
      }

      get enableVariantGroupImages() {
        return this.dataset.enableVariantGroupImages === "true" || false;
      }

      get disableDefaultSelectVariant() {
        return this.dataset.disableDefaultSelectVariant === "true" || false;
      }

      get productForm() {
        return this.querySelector("product-form");
      }

      get quantityInput() {
        return this.querySelector("quantity-input input");
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-picker [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getVariantGroupImages() {
        return (
          JSON.parse(
            this.querySelector('#VariantGroupImages[type="application/json"]')
              .textContent
          ) || {}
        );
      }

      getProductOptionByName(optionName, productData) {
        for (var i = 0; i < productData.options.length; i++) {
          var option = productData.options[i];
          if (option["name"] === optionName) {
            return option;
          }
        }
        return null;
      }

      async getProductData() {
        try {
          const response = await fetch(this.dataset.productUrl + ".js");
          if (!response.ok) {
            throw new Error(
              `Bad HTTP status of the request (${response.status}).`
            );
          }
          const data = await response.json();
          return data;
        } catch (err) {
          console.log(
            `An error occurred while processing the request. Error message: ${err.message}`
          );
        }
      }

      async init() {
        this.onVariantChangeUnsubscriber = window.FoxThemeEvents.subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
        // Cached selections carry cart-dependent details; drop them on change.
        this.cacheResetUnsubscriber = window.FoxThemeEvents.subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          () => this.sectionCache.clear()
        );

        if (this.enableVariantGroupImages) {
          this.variantGroupImages = this.getVariantGroupImages();
        }

        this.addEventListener("product-media:loaded", (evt) => {
          if (this.enableVariantGroupImages && this.variantGroupImages.enable) {
            this.updateMedia(evt.detail.currentVariant);
          }
        });

        this.productData = await this.getProductData();
        this.Notification = window.FoxTheme.Notification;

        if (this.productData && this.disableDefaultSelectVariant) {
          this.handleDisableDefaultSelectVariant();
        }
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          __reInitTooltip(newNode);
          window?.Shopify?.PaymentButton?.init();
          console.log("init");
          window?.ProductModel?.loadShopifyXR();
        });
      }

      connectedCallback() {
        this.addEventListener("click", this.handleSelectionRetry);
        this.addEventListener("pointerover", this.handleOptionIntent);
        this.addEventListener("pointerdown", this.handleOptionIntent);
        this.addEventListener("focusin", this.handleOptionIntent);
        this.addEventListener("pointerout", this.cancelOptionIntent);
        this.initializeProductSwapUtility();
        this.init();

        this.initQuantityHandlers();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleSelectionRetry);
        this.removeEventListener("pointerover", this.handleOptionIntent);
        this.removeEventListener("pointerdown", this.handleOptionIntent);
        this.removeEventListener("focusin", this.handleOptionIntent);
        this.removeEventListener("pointerout", this.cancelOptionIntent);
        clearTimeout(this.intentTimer);
        clearTimeout(this.loadingTimer);
        this.abortController?.abort();
        this.onVariantChangeUnsubscriber?.();
        this.cacheResetUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      handleDisableDefaultSelectVariant() {
        const stickyAtc = document.querySelector(".sticky-atc-bar");
        let urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("variant")) return;

        if (stickyAtc) {
          stickyAtc.classList.add("f-hidden");
        }

        this.inventoryStatus && this.inventoryStatus.setAttribute("hidden", "");
        this.sku && this.sku.classList.add("hidden");
        this.availability && this.availability.classList.add("hidden");

        let isVariantChange = false;
        this.pickerFields = this.querySelectorAll("[data-picker-field]");

        this.pickerFields &&
          this.pickerFields.forEach((field) => {
            let optionName = field.dataset.optionName;
            let optionInfo = this.getProductOptionByName(
              optionName,
              this.productData
            );

            if (optionInfo && optionInfo.values.length > 1) {
              isVariantChange = true;
              let pickerType = field.dataset.pickerField;
              switch (pickerType) {
                case "select":
                  let selectBox = field.querySelector("select");
                  let option = document.createElement("option");
                  option.text = this.dataset.variantOptionNoneText;
                  option.setAttribute("disabled", "");
                  option.setAttribute("selected", "");

                  selectBox.add(option, 0);
                  break;
                default:
                  let checkedInputs = field.querySelectorAll("input:checked");
                  checkedInputs &&
                    checkedInputs.forEach(function (input) {
                      input.removeAttribute("checked");
                    });
                  break;
              }

              field.dataset.selectedValue = "";
              if (field.querySelector(".selected-value")) {
                field.querySelector(".selected-value").textContent = "";
              }
            }
          });

        if (isVariantChange) {
          document
            .querySelectorAll(
              `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}-dynamic-checkout`
            )
            .forEach((productForm) => {
              const input = productForm.querySelector('input[name="id"]');
              input.value = "";
            });

          const atcButton = this.querySelector('[name="add"]');
          const buyNowButton = this.querySelector(
            ".shopify-payment-button__button"
          );

          atcButton.addEventListener(
            "click",
            this.handleDisableDefaultSelectVariantMessage.bind(this)
          );
          buyNowButton &&
            buyNowButton.addEventListener(
              "click",
              this.handleDisableDefaultSelectVariantMessage.bind(this)
            );
        }
      }

      handleDisableDefaultSelectVariantMessage(evt) {
        if (!this.currentVariant) {
          evt.preventDefault();
          this.pickerFields &&
            this.pickerFields.forEach((pickerField) => {
              if ("" === pickerField.dataset.selectedValue) {
                pickerField
                  .closest(".variant-picker__field-wrapper")
                  .classList.add("is-error");
              }
            });

          this.Notification.show({
            target: this.productForm,
            method: "appendChild",
            type: "warning",
            last: 3000,
            message: this.dataset.variantSelectionNeededText,
          });
        }
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;
        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.productUrl;
        const shouldSwapProduct = this.dataset.productUrl !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
          showGalleryLoader: !shouldSwapProduct && !shouldFetchFullPage && this.mediaWillChange(selectedOptionValues),
        });
      }

      mediaWillChange(selectedOptionValues) {
        const variantData = this.productMedia?.variantData;
        if (!variantData) return true;

        // Map option value IDs → text values using picker inputs
        const idToText = {};
        this.querySelectorAll('[data-option-value-id]').forEach(el => {
          idToText[el.dataset.optionValueId] = el.value;
        });

        const selectedTexts = selectedOptionValues.map(id => idToText[id]);
        if (selectedTexts.some(v => v === undefined)) return true;

        const matchingVariant = variantData.find(variant =>
          [variant.option1, variant.option2, variant.option3]
            .filter(v => v != null)
            .every((opt, i) => opt === selectedTexts[i])
        );
        if (!matchingVariant) return true;

        if (this.variantGroupImages?.enable) {
          const currentIds = [...(this.productMedia?.querySelectorAll('.f-product__media') || [])].map(m => m.dataset.mediaId);
          const mapping = this.variantGroupImages.mapping.find(m => Number(m.id) === matchingVariant.id);
          if (!mapping) return true;
          return !this.areArraysEquivalent(mapping.media, currentIds);
        }

        // Without variantGroupImages: media only changes if featured_media changes
        const currentVariant = this.productMedia?.currentVariant;
        return matchingVariant.featured_media?.id !== currentVariant?.featured_media?.id;
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";

          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            const currentShareModal = document.getElementById(
              `PopupModal-Sharing-${this.sectionId}`
            );
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;
            if (currentShareModal) document.body.removeChild(currentShareModal);

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById("shopify-section-sticky-atc-bar"),
              html.getElementById("shopify-section-sticky-atc-bar"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);
          this.toolTip = __reInitTooltip(this);

          const metaTitle = html.querySelector('.meta-title');
          if (metaTitle) {
            document.title = metaTitle.innerHTML;
          }

          if (!variant) {
            this.setUnavailable();
            this.productMedia?.removeAttribute('data-variant-loading');
            return;
          }

          const sourceGallery = html.querySelector(`[id^="MediaGallery-${this.sectionId}"]`);
          const destGallery = this.querySelector(`[id^="MediaGallery-${this.sectionId}"]`);
          if (sourceGallery && destGallery) {
            const sourceIds = [...sourceGallery.querySelectorAll('.f-product__media')].map(el => el.dataset.mediaId).join(',');
            const destIds = [...destGallery.querySelectorAll('.f-product__media')].map(el => el.dataset.mediaId).join(',');
            if (sourceIds !== destIds) {
              destGallery.outerHTML = sourceGallery.outerHTML;
              this.productMedia?.removeAttribute('data-media-loading');
            } else {
              this.updateMedia(variant);
            }
          } else {
            this.updateMedia(variant);
          }

          const updateSourceFromDestination = (id) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.sectionId}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.removeAttribute("hidden");
            }
          };

          updateSourceFromDestination("Price");
          updateSourceFromDestination("ProductHeading");
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("PricePerItem");
          updateSourceFromDestination("Inventory");
          updateSourceFromDestination("Sku");
          updateSourceFromDestination("Availability");

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination("QuantityRules");
          updateSourceFromDestination("QuantityRulesCart");
          updateSourceFromDestination("VolumeNote");
          updateSourceFromDestination("Description");

          const addButtonUpdated = html.getElementById(
            `ProductSubmitButton-${this.sectionId}`
          );
          this.updateButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute("disabled") : true,
            window.FoxThemeStrings.soldOut
          );
          this.currentVariant = variant;

          window.FoxThemeEvents.emit(PUB_SUB_EVENTS.variantChange, {
            data: { variant, sectionId: this.sectionId, html },
          });

          document.dispatchEvent(
            new CustomEvent("variant:changed", {
              detail: {
                variant: this.currentVariant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        // Alternate product templates (?view=matt-graham): section ids belong to that template,
        // so the re-render must ask for the same view or it comes back empty ("Sold out").
        const view = new URL(window.location.href).searchParams.get("view");
        view && params.push(`view=${encodeURIComponent(view)}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      findFallbackOptionValues(html) {
        const variantPicker = html.querySelector("variant-picker");
        if (!variantPicker) return null;

        let needsFallback = false;
        const correctedValues = [];

        variantPicker.querySelectorAll("[data-picker-field]").forEach((field) => {
          if (field.dataset.pickerField === "radio") {
            const checked = field.querySelector("input[type='radio']:checked");
            if (checked?.classList.contains("disabled")) {
              const firstAvailable = field.querySelector(
                "input[type='radio']:not(.disabled)"
              );
              if (firstAvailable?.dataset.optionValueId) {
                correctedValues.push(firstAvailable.dataset.optionValueId);
                needsFallback = true;
                return;
              }
            }
            if (checked?.dataset.optionValueId) {
              correctedValues.push(checked.dataset.optionValueId);
            }
          } else if (field.dataset.pickerField === "select") {
            const select = field.querySelector("select");
            if (select) {
              const opts = Array.from(select.options);
              const selected = opts.find((o) => o.selected);
              if (selected?.disabled) {
                const firstAvailable = opts.find((o) => !o.disabled);
                if (firstAvailable?.dataset.optionValueId) {
                  correctedValues.push(firstAvailable.dataset.optionValueId);
                  needsFallback = true;
                  return;
                }
              }
              if (selected?.dataset.optionValueId) {
                correctedValues.push(selected.dataset.optionValueId);
              }
            }
          }
        });

        return needsFallback ? correctedValues : null;
      }

      prefetchOption(input) {
        const productUrl = input.dataset.productUrl || this.dataset.productUrl;
        const picker = this.variantSelectors;
        if (!picker || productUrl !== this.dataset.productUrl || this.pendingRequestUrl) return;
        const fieldInputs = [...picker.querySelectorAll("input[type='radio']")].filter(
          (field) => field.name === input.name
        );
        const current = fieldInputs.find((field) => field.checked) || fieldInputs[0];
        const optionValues = picker.selectedOptionValues;
        const index = optionValues.indexOf(current?.dataset.optionValueId);
        if (index === -1) return;
        optionValues[index] = input.dataset.optionValueId;
        const requestUrl = this.buildRequestUrlWithParams(productUrl, optionValues);
        if (this.cachedSectionText(requestUrl)) return;
        this.cacheSectionText(requestUrl, this.requestSectionText(requestUrl))
          .then((text) => this.preloadGalleryImage(text))
          .catch(() => {});
      }

      // Warm the first photo of a prefetched selection so the new gallery
      // shows it as soon as it swaps in.
      preloadGalleryImage(text) {
        const whenIdle = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));
        whenIdle(() => {
          const gallerySelector = `[id^="MediaGallery-${this.sectionId}"] .f-product__media img`;
          const source = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector(gallerySelector);
          const current = this.querySelector(gallerySelector);
          if (!source || source.getAttribute("src") === current?.getAttribute("src")) return;
          const image = new Image();
          ["sizes", "srcset", "src"].forEach((name) => {
            if (source.hasAttribute(name)) image.setAttribute(name, source.getAttribute(name));
          });
        });
      }

      cachedSectionText(requestUrl) {
        const entry = this.sectionCache.get(requestUrl);
        if (entry && Date.now() - entry.at < this.sectionCacheTtl) return entry.text;
        this.sectionCache.delete(requestUrl);
        return null;
      }

      cacheSectionText(requestUrl, text) {
        for (const [url, entry] of this.sectionCache) {
          if (url === requestUrl || Date.now() - entry.at >= this.sectionCacheTtl) {
            this.sectionCache.delete(url);
          }
        }
        this.sectionCache.set(requestUrl, { at: Date.now(), text });
        if (this.sectionCache.size > 12) {
          this.sectionCache.delete(this.sectionCache.keys().next().value);
        }
        text.catch(() => {
          if (this.sectionCache.get(requestUrl)?.text === text) this.sectionCache.delete(requestUrl);
        });
        return text;
      }

      async requestSectionText(requestUrl, signal) {
        let response = await fetch(requestUrl, { signal });
        const fallbackUrl = new URL(requestUrl, window.location.origin);
        // Shopify can reject option_values + section_id while the same
        // selection resolves on the full product page. Never interpret an
        // HTTP error document as a genuinely unavailable variant.
        if (!response.ok && fallbackUrl.searchParams.has("section_id")) {
          fallbackUrl.searchParams.delete("section_id");
          response = await fetch(fallbackUrl.toString(), { signal });
        }
        if (!response.ok) {
          throw new Error(`Product selection request failed (${response.status}).`);
        }
        return response.text();
      }

      async fetchProductInfo(requestUrl, signal) {
        // Reuse a prefetched or recent response for the same selection.
        const cached = this.cachedSectionText(requestUrl);
        let text = cached ? await cached.catch(() => null) : null;
        if (!text) {
          text = await this.requestSectionText(requestUrl, signal);
          if (requestUrl.includes("section_id=")) {
            this.cacheSectionText(requestUrl, Promise.resolve(text));
          }
        }
        if (signal.aborted) throw new DOMException("Request aborted", "AbortError");
        const html = new DOMParser().parseFromString(text, "text/html");
        if (!html.querySelector("variant-picker [data-selected-variant]")) {
          throw new Error("Product selection response is missing variant data.");
        }
        return html;
      }

      renderProductInfo({ requestUrl, targetId, callback, showGalleryLoader = true }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        const buyButtonsBlock = this.querySelector('.f-product-single__block--buy_buttons');
        const errorPanel = this.querySelector('[data-selection-error]');
        const selectionStatus = this.querySelector('[data-selection-status]');
        this.failedSelectionRequest = null;
        if (errorPanel) errorPanel.hidden = true;
        this.updateButton(true, 'Updating options…');
        buyButtonsBlock?.setAttribute('aria-busy', 'true');
        buyButtonsBlock?.setAttribute('data-variant-loading', '');

        // Show the status line and page overlay only when a change is slow
        // enough to notice; prefetched selections usually render at once.
        clearTimeout(this.loadingTimer);
        this.loadingTimer = setTimeout(() => {
          if (signal.aborted) return;
          if (selectionStatus) selectionStatus.hidden = false;
          if (showGalleryLoader) {
            this.productMedia?.setAttribute('data-variant-loading', '');
          }
        }, 300);
        const render = (html) => {
          clearTimeout(this.loadingTimer);
          callback(html);
        };
        return this.fetchProductInfo(requestUrl, signal)
          .then((html) => {
            this.pendingRequestUrl = null;

            const fallbackValues = this.findFallbackOptionValues(html);
            if (fallbackValues) {
              return this.fetchProductInfo(
                this.buildRequestUrlWithParams(
                  this.dataset.productUrl,
                  fallbackValues,
                  false
                ),
                signal
              )
                .then(render);
            }

            render(html);
          })
          .then(() => {
            if (signal.aborted) return;
            buyButtonsBlock?.removeAttribute('data-variant-loading');
            buyButtonsBlock?.removeAttribute('aria-busy');
            if (selectionStatus) selectionStatus.hidden = true;
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (signal.aborted || error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              clearTimeout(this.loadingTimer);
              this.productMedia?.removeAttribute('data-variant-loading');
              buyButtonsBlock?.removeAttribute('data-variant-loading');
              buyButtonsBlock?.removeAttribute('aria-busy');
              if (selectionStatus) selectionStatus.hidden = true;
              // Do not let a stale form add a different variant from the
              // shopper's visible selection after a network failure.
              this.updateVariantInputs(null);
              this.updateButton(true, errorPanel ? "Selection not loaded" : "Please refresh to select options");
              if (errorPanel) {
                this.failedSelectionRequest = { requestUrl, targetId, callback, showGalleryLoader };
                errorPanel.hidden = false;
              }
              this.currentVariant = null;
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-picker");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}${this.viewParam(variantId ? "&" : "?")}`
        );
      }

      viewParam(prefix) {
        const view = new URL(window.location.href).searchParams.get("view");
        return view ? `${prefix}view=${encodeURIComponent(view)}` : "";
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(
          `ShareButton-${this.dataset.section}`
        );
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(
          `${window.shopUrl}${this.dataset.url}?variant=${variantId}`
        );
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}-dynamic-checkout`
          )
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? "";
            input.dispatchEvent(new Event("change", { bubbles: true }));
          });
      }

      updateButton(disable = true, text) {
        const productForm = document.getElementById(
          `product-form-${this.sectionId}`
        );
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector(
          '[name="add"] > span:not(.f-icon)'
        );

        if (!addButton) return;
        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute("disabled");
          addButtonText.textContent = window.FoxThemeStrings.addToCart;
        }
      }

      updateMedia(variant) {
        if (!this.productMedia) return;
        this.productMedia.removeAttribute('data-variant-loading');

        if (this.variantGroupImages && this.variantGroupImages.enable) {
          const isCarousel = this.productMedia.querySelector(".flickity-slider")
            ? true
            : false;
          const initalMediaIds = this.productMedia?.domNodes?.medias.map(
            (item) => item.dataset.mediaId
          );
          const groupImages = this.variantGroupImages.mapping.find(
            (item) => Number(item.id) === variant.id
          );
          const currentVariantMedia = groupImages
            ? groupImages.media
            : this.allMediaIds;

          if (!this.areArraysEquivalent(currentVariantMedia, initalMediaIds)) {
            const mainMedia = this.productMedia.querySelector(
              ".f-product__media-list"
            );
            const thumbMedia = this.productMedia.querySelector(
              ".f-product__media-thumbnails"
            );
            if (isCarousel) {
              const mainFlickity = window.FoxTheme.Flickity.data(mainMedia);

              let mediaToRemove =
                mainMedia.querySelectorAll(".f-product__media");
              mediaToRemove &&
                mediaToRemove.forEach(function (slide) {
                  mainFlickity.remove(slide);
                });
              let mediaToShow = this.filteredMediaForSelectedVariant(
                this.productMedia.domNodes.medias,
                currentVariantMedia
              );

              mediaToShow.forEach(function (slide, index) {
                const zoomButton = slide.querySelector(".js-photoswipe--zoom");
                if (zoomButton) {
                  zoomButton.setAttribute("data-media-index", index);
                }
                slide.setAttribute("data-media-index", index);
                slide.setAttribute("data-index", index);
                mainFlickity.append(slide);
              });

              if (thumbMedia && this.productMedia.domNodes.thumbnailItems) {
                const thumbnailsFlickity =
                  window.FoxTheme.Flickity.data(thumbMedia);

                let thumbsToRemove = thumbMedia.querySelectorAll(
                  ".f-product__media-thumbnails-item"
                );
                thumbsToRemove &&
                  thumbsToRemove.forEach(function (slide) {
                    thumbnailsFlickity.remove(slide);
                  });

                let thumbsToShow = this.filteredMediaForSelectedVariant(
                  this.productMedia.domNodes.thumbnailItems,
                  currentVariantMedia
                );
                thumbsToShow.forEach(function (slide, index) {
                  slide.setAttribute("data-thumbnail-index", index);
                  slide.setAttribute("data-index", index);
                  thumbnailsFlickity.append(slide);
                });

                thumbMedia.classList.remove(
                  "disable-transition",
                  "md:disable-transition"
                );
                if (thumbsToShow.length < 5) {
                  thumbMedia.classList.add("disable-transition");
                }
                if (thumbsToShow.length < 6) {
                  thumbMedia.classList.add("md:disable-transition");
                }
                thumbnailsFlickity.option({
                  wrapAround: thumbsToShow.length > 5,
                });
                thumbnailsFlickity.reloadCells();
                thumbnailsFlickity.reposition();
              }

              mainFlickity.select(0, false, false);
            } else {
              let mediaToShow = this.filteredMediaForSelectedVariant(
                this.productMedia.domNodes.medias,
                currentVariantMedia
              );
              mainMedia.innerHTML = "";
              mediaToShow.forEach((item, index) => {
                const zoomButton = item.querySelector(".js-photoswipe--zoom");
                if (zoomButton) {
                  zoomButton.setAttribute("data-media-index", index);
                }
                item.setAttribute("data-media-index", index);
                item.setAttribute("data-index", index);
                mainMedia.append(item);
              });
            }

            // Re-init Photoswipe
            if (this.productMedia && this.productMedia.enableZoom) {
              this.productMedia.lightbox &&
                this.productMedia.lightbox.destroy();
              this.productMedia.initImageZoom();
            }
          }

          this.productMedia.removeAttribute("data-media-loading");
        } else {
          if (!variant.featured_media) return;
          if (typeof this.productMedia.setActiveMedia === "function") {
            this.productMedia.setActiveMedia(variant.featured_media.id);
          }
        }
      }

      setUnavailable() {
        this.updateButton(true, window.FoxThemeStrings.unavailable);

        const selectors = [
          "Price",
          "Inventory",
          "Sku",
          "PricePerItem",
          "VolumeNote",
          "Volume",
          "QuantityRules",
          "QuantityRulesCart",
        ]
          .map((id) => `#${id}-${this.sectionId}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach((selector) => selector.setAttribute("hidden", ""));
      }

      areArraysEquivalent(firstArray, secondArray) {
        if (!Array.isArray(firstArray) || !Array.isArray(secondArray)) {
          return false;
        }

        if (firstArray.length !== secondArray.length) {
          return false;
        }

        const sortedFirstArray = [...firstArray].sort();
        const sortedSecondArray = [...secondArray].sort();

        return sortedFirstArray.every(
          (value, index) => value === sortedSecondArray[index]
        );
      }

      filteredMediaForSelectedVariant(items, currentVariantMedia) {
        let index = 0;
        let prioritizedResults = [];
        let otherResults = [];
        items.forEach((item) => {
          const dataIdMedia = item.dataset.mediaId;
          if (currentVariantMedia && currentVariantMedia.length > 0) {
            if (currentVariantMedia.includes(dataIdMedia)) {
              item.dataset.index = index++;
              prioritizedResults.push(item);
            }

            if (item.dataset.mediaType !== "image") {
              item.dataset.index = index++;
              otherResults.push(item);
            }
          } else {
            item.dataset.index = index++;
            prioritizedResults.push(item);
          }
        });

        const sortByVariantOrder = (a, b) => {
          const indexA = currentVariantMedia.indexOf(a.dataset.mediaId);
          const indexB = currentVariantMedia.indexOf(b.dataset.mediaId);
          return indexA - indexB;
        };

        prioritizedResults.sort(sortByVariantOrder);

        const results = [...prioritizedResults, ...otherResults];

        return results;
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute("data-original-section-id")) {
          this.cartUpdateUnsubscriber = FoxThemeEvents.subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxThemeEvents.emit(PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        fetch(
          `${this.getAttribute(
            "data-product-url"
          )}?variant=${currentVariantId}&section_id=${this.sectionId}${this.viewParam("&")}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(
              this.sectionId,
              this.productId,
              parsedHTML
            );
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {});
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxThemeEvents.emit(PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}
